import React, { useEffect, useMemo, useRef, useState } from 'react';
import './RoomBrandingPanel.css';
import {
  buildBrandingSavePayload,
  fileToBrandingBackgroundDataUrl,
  fileToBrandingLogoDataUrl,
  type RoomBranding,
} from '../utils/roomBranding';
import {
  AI_MAX_COMPANY_QUESTIONS,
  companyQuestionsEditErrorMessage,
  parseCompanyQuestionsEdit,
  serializeCompanyQuestionsForEdit,
} from '../utils/companyQuestionsEdit';

export interface AiPromptUsage {
  used: number;
  limit: number;
}

export interface CompanyQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: string;
  points?: number;
  timeLimit?: number;
  category?: string;
  source?: string;
  generatedAt?: number;
  interesting?: string;
}

interface RoomBrandingPanelProps {
  roomName: string;
  branding?: RoomBranding | null;
  companyQuestions?: CompanyQuestion[];
  aiPromptUsage?: AiPromptUsage | null;
  /** True when a live room is hosted (logo/background can be applied immediately). */
  isLiveRoom: boolean;
  /** Live room game state — edits blocked while `playing`. */
  gameState?: 'waiting' | 'playing' | 'finished' | string;
  /** When set, renders as a modal overlay. */
  onClose?: () => void;
  socket?: {
    emit: (event: string, data: any) => void;
    on: (event: string, callback: (data: any) => void) => void;
    off?: (event: string, callback?: (data: any) => void) => void;
  } | null;
  onUpdated?: (payload: {
    branding?: RoomBranding | null;
    companyQuestions?: CompanyQuestion[];
    aiPromptUsage?: AiPromptUsage;
  }) => void;
}

const MAX_COLORS = 3;

const RoomBrandingPanel: React.FC<RoomBrandingPanelProps> = ({
  roomName,
  branding,
  companyQuestions: questionsProp,
  aiPromptUsage: usageProp,
  isLiveRoom,
  gameState = 'waiting',
  onClose,
  socket,
  onUpdated,
}) => {
  const isModal = !!onClose;
  const [displayName, setDisplayName] = useState(branding?.displayName ?? '');
  const [website, setWebsite] = useState(branding?.website ?? '');
  const [allowWebsiteCrawl, setAllowWebsiteCrawl] = useState(!!branding?.allowWebsiteCrawl);
  const [colors, setColors] = useState<string[]>((branding?.colors ?? []).slice(0, MAX_COLORS));
  const [logoDataUrl, setLogoDataUrl] = useState<string | null | undefined>(
    branding?.logoDataUrl
  );
  const [backgroundDataUrl, setBackgroundDataUrl] = useState<string | null | undefined>(
    branding?.backgroundDataUrl
  );
  const [companyQuestions, setCompanyQuestions] = useState<CompanyQuestion[]>(questionsProp ?? []);
  const [aiPromptUsage, setAiPromptUsage] = useState<AiPromptUsage>(
    usageProp ?? { used: 0, limit: 3 }
  );

  const [logoBusy, setLogoBusy] = useState(false);
  const [backgroundBusy, setBackgroundBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [clearingQuestions, setClearingQuestions] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [questionsEditText, setQuestionsEditText] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  const dirtyRef = useRef(false);

  const questionsEditable = !isLiveRoom || gameState === 'waiting' || gameState === 'finished';
  const questionsEditValidation = useMemo(
    () => parseCompanyQuestionsEdit(questionsEditText, companyQuestions),
    [questionsEditText, companyQuestions]
  );
  const questionsEditFormatError =
    editingQuestions && !questionsEditValidation.ok
      ? companyQuestionsEditErrorMessage(questionsEditValidation)
      : null;

  // Hydrate from props when room changes or external refresh (unless dirty local edits)
  useEffect(() => {
    if (dirtyRef.current) return;
    setDisplayName(branding?.displayName ?? '');
    setWebsite(branding?.website ?? '');
    setAllowWebsiteCrawl(!!branding?.allowWebsiteCrawl);
    setColors((branding?.colors ?? []).slice(0, MAX_COLORS));
    setLogoDataUrl(branding?.logoDataUrl);
    setBackgroundDataUrl(branding?.backgroundDataUrl);
  }, [roomName, branding]);

  useEffect(() => {
    setCompanyQuestions(questionsProp ?? []);
  }, [questionsProp]);

  useEffect(() => {
    if (usageProp) setAiPromptUsage(usageProp);
  }, [usageProp]);

  useEffect(() => {
    if (!questionsEditable && editingQuestions) {
      setEditingQuestions(false);
      setQuestionsEditText('');
    }
  }, [questionsEditable, editingQuestions]);

  useEffect(() => {
    if (!socket?.on) return;

    const handleBrandingResult = (data: {
      success: boolean;
      error?: string;
      branding?: RoomBranding | null;
      companyQuestions?: CompanyQuestion[];
      aiPromptUsage?: AiPromptUsage;
    }) => {
      setSaving(false);
      if (!data.success) {
        setError(data.error || 'Failed to save branding');
        return;
      }
      dirtyRef.current = false;
      setMessage('Branding saved');
      setError(null);
      if (data.branding !== undefined) {
        setDisplayName(data.branding?.displayName ?? '');
        setWebsite(data.branding?.website ?? '');
        setAllowWebsiteCrawl(!!data.branding?.allowWebsiteCrawl);
        setColors(data.branding?.colors ?? []);
        setLogoDataUrl(data.branding?.logoDataUrl);
        setBackgroundDataUrl(data.branding?.backgroundDataUrl);
      }
      if (data.companyQuestions) setCompanyQuestions(data.companyQuestions);
      if (data.aiPromptUsage) setAiPromptUsage(data.aiPromptUsage);
      onUpdated?.({
        branding: data.branding,
        companyQuestions: data.companyQuestions,
        aiPromptUsage: data.aiPromptUsage,
      });
    };

    const handleResetResult = (data: {
      success: boolean;
      error?: string;
      aiPromptUsage?: AiPromptUsage;
    }) => {
      setResetting(false);
      if (!data.success) {
        setError(data.error || 'Failed to reset AI usage');
        return;
      }
      setMessage('AI generation usage reset');
      setError(null);
      if (data.aiPromptUsage) {
        setAiPromptUsage(data.aiPromptUsage);
        onUpdated?.({ aiPromptUsage: data.aiPromptUsage });
      }
    };

    const handleQuestionsResult = (data: {
      success: boolean;
      error?: string;
      companyQuestions?: CompanyQuestion[];
    }) => {
      setClearingQuestions(false);
      setSavingQuestions(false);
      if (!data.success) {
        setError(data.error || 'Failed to update questions');
        return;
      }
      const next = data.companyQuestions ?? [];
      setCompanyQuestions(next);
      setEditingQuestions(false);
      setQuestionsEditText('');
      setShowQuestions(next.length > 0);
      setMessage(
        next.length === 0 ? 'Company questions cleared' : `Saved ${next.length} company questions`
      );
      setError(null);
      onUpdated?.({ companyQuestions: next });
    };

    socket.on('admin-update-room-branding-result', handleBrandingResult);
    socket.on('admin-reset-ai-prompt-usage-result', handleResetResult);
    socket.on('admin-update-company-questions-result', handleQuestionsResult);
    return () => {
      socket.off?.('admin-update-room-branding-result', handleBrandingResult);
      socket.off?.('admin-reset-ai-prompt-usage-result', handleResetResult);
      socket.off?.('admin-update-company-questions-result', handleQuestionsResult);
    };
  }, [socket, onUpdated]);

  const markDirty = () => {
    dirtyRef.current = true;
    setMessage(null);
    setError(null);
  };

  const remainingPrompts = Math.max(0, aiPromptUsage.limit - aiPromptUsage.used);

  const handleAddColor = () => {
    if (colors.length >= MAX_COLORS) return;
    markDirty();
    setColors([...colors, '#2563EB']);
  };

  const handleColorChange = (index: number, value: string) => {
    markDirty();
    const next = [...colors];
    next[index] = value;
    setColors(next);
  };

  const handleRemoveColor = (index: number) => {
    markDirty();
    setColors(colors.filter((_, i) => i !== index));
  };

  const handleLogoUpload = async (file: File | null) => {
    if (!file) return;
    setLogoBusy(true);
    setError(null);
    try {
      const dataUrl = await fileToBrandingLogoDataUrl(file);
      markDirty();
      setLogoDataUrl(dataUrl);
    } catch {
      setError('Could not process logo image');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleBackgroundUpload = async (file: File | null) => {
    if (!file) return;
    setBackgroundBusy(true);
    setError(null);
    try {
      const dataUrl = await fileToBrandingBackgroundDataUrl(file);
      markDirty();
      setBackgroundDataUrl(dataUrl);
    } catch {
      setError('Could not process background image');
    } finally {
      setBackgroundBusy(false);
    }
  };

  const handleSave = () => {
    if (!socket?.emit) {
      setError('Not connected');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    socket.emit('admin-update-room-branding', {
      roomName,
      branding: buildBrandingSavePayload({
        displayName,
        colors,
        website,
        allowWebsiteCrawl,
        logoDataUrl,
        backgroundDataUrl,
      }),
    });
  };

  const handleResetUsage = (resetLimitToIncluded: boolean) => {
    if (!socket?.emit) {
      setError('Not connected');
      return;
    }
    const confirmMsg = resetLimitToIncluded
      ? `Reset AI usage for ${roomName} to 0 of 3 (also resets top-up limit)?`
      : `Reset used generations for ${roomName} to 0 (keep current limit of ${aiPromptUsage.limit})?`;
    if (!window.confirm(confirmMsg)) return;
    setResetting(true);
    setError(null);
    setMessage(null);
    socket.emit('admin-reset-ai-prompt-usage', { roomName, resetLimitToIncluded });
  };

  const handleClearQuestions = () => {
    if (!socket?.emit) {
      setError('Not connected');
      return;
    }
    if (!questionsEditable) {
      setError('Finish or return to lobby before clearing questions');
      return;
    }
    if (!window.confirm(`Clear all ${companyQuestions.length} company questions for ${roomName}?`)) {
      return;
    }
    setClearingQuestions(true);
    setError(null);
    setMessage(null);
    socket.emit('admin-update-company-questions', {
      roomName,
      companyQuestions: [],
    });
  };

  const startEditingQuestions = () => {
    if (!questionsEditable || !socket) return;
    setError(null);
    setMessage(null);
    setShowQuestions(true);
    setQuestionsEditText(serializeCompanyQuestionsForEdit(companyQuestions));
    setEditingQuestions(true);
  };

  const cancelEditingQuestions = () => {
    setEditingQuestions(false);
    setQuestionsEditText('');
    setSavingQuestions(false);
    setError(null);
  };

  const handleSaveEditedQuestions = () => {
    if (!socket?.emit) {
      setError('Not connected');
      return;
    }
    if (!questionsEditable) {
      setError('Finish or return to lobby before editing questions');
      return;
    }
    const parsed = parseCompanyQuestionsEdit(questionsEditText, companyQuestions);
    if (!parsed.ok) {
      setError(companyQuestionsEditErrorMessage(parsed));
      return;
    }
    if (parsed.questions.length > AI_MAX_COMPANY_QUESTIONS) {
      setError(`Room can store at most ${AI_MAX_COMPANY_QUESTIONS} company questions`);
      return;
    }
    setSavingQuestions(true);
    setError(null);
    setMessage(null);
    socket.emit('admin-update-company-questions', {
      roomName,
      companyQuestions: parsed.questions,
    });
  };

  const panel = (
    <div className={`detail-panel branding-panel${isModal ? ' branding-panel--modal' : ''}`}>
      <div className="panel-header">
        <h3 className="panel-title">
          <span className="title-icon">🎨</span>
          Branding &amp; company content
          <span className="branding-room-code">#{roomName}</span>
        </h3>
        {onClose && (
          <button type="button" className="branding-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>
      <div className="panel-content">
        {!isLiveRoom && (
          <p className="branding-note">
            Room is offline. Name, colors, website, crawl consent, and questions still save for this
            code. Logo/background apply when the room is live (or after host recreates and re-uploads).
          </p>
        )}

        <section className="branding-section">
          <h4 className="branding-section-title">Colors / palette</h4>
          <p className="branding-hint">Add up to 3 colors: primary, secondary, and tertiary accents for the room.</p>
          <div className="branding-colors">
            {colors.map((color, index) => (
              <div key={`${color}-${index}`} className="branding-color-row">
                <input
                  type="color"
                  value={/^#[0-9A-Fa-f]{6}$/.test(color) ? color : '#2563EB'}
                  onChange={(e) => handleColorChange(index, e.target.value.toUpperCase())}
                  aria-label={`Color ${index + 1}`}
                />
                <input
                  type="text"
                  className="branding-input branding-input--mono"
                  value={color}
                  onChange={(e) => handleColorChange(index, e.target.value)}
                  maxLength={7}
                />
                <button type="button" className="branding-link-btn" onClick={() => handleRemoveColor(index)}>
                  Remove
                </button>
              </div>
            ))}
            {colors.length < MAX_COLORS && (
              <button type="button" className="branding-add-color" onClick={handleAddColor}>
                + Add color
              </button>
            )}
          </div>
        </section>

        <section className="branding-assets">
          <div className="branding-asset">
            <label className="branding-label">Logo</label>
            <div className="branding-asset-row">
              <div className="branding-preview">
                {logoDataUrl ? (
                  <img src={logoDataUrl} alt="Logo" />
                ) : (
                  <span className="branding-preview-empty">📷</span>
                )}
              </div>
              <div className="branding-asset-actions">
                <button
                  type="button"
                  className="branding-secondary-btn"
                  disabled={logoBusy}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoBusy ? 'Processing…' : 'Upload logo'}
                </button>
                {logoDataUrl && (
                  <button
                    type="button"
                    className="branding-link-btn"
                    onClick={() => {
                      markDirty();
                      setLogoDataUrl(null);
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  void handleLogoUpload(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </div>
          </div>

          <div className="branding-asset">
            <label className="branding-label">Background</label>
            <div className="branding-asset-row">
              <div className="branding-preview branding-preview--wide">
                {backgroundDataUrl ? (
                  <img src={backgroundDataUrl} alt="Background" />
                ) : (
                  <span className="branding-preview-empty">📷</span>
                )}
              </div>
              <div className="branding-asset-actions">
                <button
                  type="button"
                  className="branding-secondary-btn"
                  disabled={backgroundBusy}
                  onClick={() => backgroundInputRef.current?.click()}
                >
                  {backgroundBusy ? 'Processing…' : 'Upload background'}
                </button>
                {backgroundDataUrl && (
                  <button
                    type="button"
                    className="branding-link-btn"
                    onClick={() => {
                      markDirty();
                      setBackgroundDataUrl(null);
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={backgroundInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  void handleBackgroundUpload(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </div>
            <p className="branding-hint">Shown behind lobby, host, and player screens.</p>
          </div>
        </section>

        <section className="branding-section">
          <label className="branding-label" htmlFor="admin-display-name">
            Company or event name
          </label>
          <input
            id="admin-display-name"
            className="branding-input"
            value={displayName}
            maxLength={60}
            onChange={(e) => {
              markDirty();
              setDisplayName(e.target.value);
            }}
            placeholder="e.g. Sixt"
          />

          <label className="branding-label" htmlFor="admin-website">
            Website
          </label>
          <input
            id="admin-website"
            className="branding-input"
            value={website}
            maxLength={200}
            onChange={(e) => {
              markDirty();
              setWebsite(e.target.value);
            }}
            placeholder="https://example.com"
          />

          <label className="branding-checkbox">
            <input
              type="checkbox"
              checked={allowWebsiteCrawl}
              onChange={(e) => {
                markDirty();
                setAllowWebsiteCrawl(e.target.checked);
              }}
            />
            <span>Allow crawling this website to generate questions</span>
          </label>
          <p className="branding-hint">
            Used with the company name to generate custom questions (when crawl is allowed).
          </p>
        </section>

        <section className="branding-company-questions">
          <h4 className="branding-cq-title">Company questions</h4>
          <p className="branding-hint">
            Trivia generated or added for this room code. Mixed into games and unlocks the branded quiz.
          </p>

          <div className="branding-usage-row">
            <span className={`branding-usage ${remainingPrompts === 0 ? 'is-empty' : ''}`}>
              {remainingPrompts} of {aiPromptUsage.limit} prompts left
              <span className="branding-usage-detail">
                {' '}
                (used {aiPromptUsage.used} / limit {aiPromptUsage.limit})
              </span>
            </span>
            <div className="branding-usage-actions">
              <button
                type="button"
                className="branding-secondary-btn"
                disabled={resetting || !socket}
                onClick={() => handleResetUsage(false)}
              >
                {resetting ? 'Resetting…' : 'Reset used → 0'}
              </button>
              <button
                type="button"
                className="branding-link-btn"
                disabled={resetting || !socket}
                onClick={() => handleResetUsage(true)}
                title="Also reset limit back to the included 3 prompts"
              >
                Reset to 3 included
              </button>
            </div>
          </div>

          <div className="branding-cq-links">
            <button
              type="button"
              className="branding-link-btn"
              disabled={companyQuestions.length === 0 || editingQuestions}
              onClick={() => setShowQuestions((v) => !v)}
            >
              {showQuestions && !editingQuestions
                ? 'Hide questions'
                : `See questions (${companyQuestions.length})`}
            </button>
            {editingQuestions ? (
              <>
                <button
                  type="button"
                  className="branding-link-btn"
                  disabled={savingQuestions}
                  onClick={cancelEditingQuestions}
                >
                  Cancel edit
                </button>
                <button
                  type="button"
                  className="branding-link-btn"
                  disabled={
                    savingQuestions ||
                    !socket ||
                    !questionsEditable ||
                    !questionsEditValidation.ok ||
                    (questionsEditValidation.ok &&
                      questionsEditValidation.questions.length > AI_MAX_COMPANY_QUESTIONS)
                  }
                  onClick={handleSaveEditedQuestions}
                >
                  {savingQuestions ? 'Saving…' : 'Save questions'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="branding-link-btn"
                disabled={!socket || !questionsEditable || clearingQuestions}
                onClick={startEditingQuestions}
                title={
                  questionsEditable
                    ? undefined
                    : 'Finish or return to lobby before editing questions'
                }
              >
                {companyQuestions.length > 0 ? 'Edit questions' : 'Add questions'}
              </button>
            )}
            {companyQuestions.length > 0 && !editingQuestions && (
              <button
                type="button"
                className="branding-link-btn branding-link-btn--danger"
                disabled={clearingQuestions || !socket || !questionsEditable}
                onClick={handleClearQuestions}
                title={
                  questionsEditable
                    ? undefined
                    : 'Finish or return to lobby before clearing questions'
                }
              >
                {clearingQuestions ? 'Clearing…' : 'Clear questions'}
              </button>
            )}
          </div>

          {!questionsEditable && (
            <p className="branding-hint branding-hint--warn">
              Game in progress — return to lobby to edit or clear company questions.
            </p>
          )}

          {showQuestions && (editingQuestions || companyQuestions.length > 0) && (
            <div className="branding-questions-wrap">
              {editingQuestions ? (
                <>
                  <textarea
                    className={`branding-questions-edit${
                      questionsEditFormatError ? ' is-invalid' : ''
                    }`}
                    value={questionsEditText}
                    onChange={(e) => {
                      setQuestionsEditText(e.target.value);
                      setError(null);
                      setMessage(null);
                    }}
                    spellCheck
                    rows={16}
                    aria-invalid={!!questionsEditFormatError}
                    aria-label="Edit company questions"
                    placeholder={
                      '1. Question text\nA. Option one\nB. Option two ✓\nC. Option three\nD. Option four\nFun fact: optional\n\n2. Next question…'
                    }
                  />
                  {questionsEditFormatError ? (
                    <p className="branding-hint branding-hint--warn" role="alert">
                      {questionsEditFormatError}
                    </p>
                  ) : (
                    <p className="branding-hint">
                      Format: numbered question, A–D options (mark correct with ✓ or an Answer:
                      line), optional Fun fact. Saving blank clears all.
                    </p>
                  )}
                </>
              ) : (
                <ul className="branding-questions-list">
                  {companyQuestions.map((q, i) => (
                    <li key={q.id || i}>
                      <strong>
                        {i + 1}. {q.question}
                      </strong>
                      <ul className="branding-question-options">
                        {(q.options?.length ? q.options : [q.correctAnswer]).map((opt) => (
                          <li
                            key={opt}
                            className={opt === q.correctAnswer ? 'is-correct' : undefined}
                          >
                            {opt}
                            {opt === q.correctAnswer ? ' ✓' : ''}
                          </li>
                        ))}
                      </ul>
                      {(q.source || q.category) && (
                        <div className="branding-question-meta">
                          {[q.source, q.category].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {q.interesting && (
                        <em className="branding-question-interesting">{q.interesting}</em>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        {(message || error) && (
          <div className={`branding-status ${error ? 'is-error' : 'is-ok'}`}>
            {error || message}
          </div>
        )}

        <div className="branding-footer">
          {onClose && (
            <button type="button" className="branding-secondary-btn" onClick={onClose}>
              Close
            </button>
          )}
          <button
            type="button"
            className="branding-primary-btn"
            disabled={saving || !socket}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save branding'}
          </button>
        </div>
      </div>
    </div>
  );

  if (!isModal) {
    return panel;
  }

  return (
    <div
      className="branding-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="presentation"
    >
      <div className="branding-modal" role="dialog" aria-modal="true" aria-label="Branding and company content">
        {panel}
      </div>
    </div>
  );
};

export default RoomBrandingPanel;
